import { useEffect, useMemo, useRef } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import { useClientsAccessRights } from '@app/hooks';

import { mapModelLeadToLeadsRegistryTableRow } from '../model/adapters/mapModelLeadToLeadsRegistryTableRow';
import {
  createLeadsRegistryInitialLoadingSkeletonRows,
  createLeadsRegistryLoadingSkeletonRows,
} from '../utils';

import { useLeadsRegistryRealtime } from './useLeadsRegistryRealtime';
import { useLeadsRegistrySearch } from './useLeadsRegistrySearch';
import { useLeadsRegistrySelection } from './useLeadsRegistrySelection';

import type {
  LeadsRegistryLeadRow,
  LeadsRegistryTableRow,
} from '../model/types';

export const useLeadsRegistryPage = () => {
  const {
    leads,
    totalCount,
    loadingMode,
    isLoadingMore,
    hasMoreLeads,
    onLoadMore,
  } = useLeadsRegistrySearch();

  useLeadsRegistryRealtime();

  const { pruneSelection } = useLeadsRegistrySelection();
  const { hasUserRightToShowContacts } = useClientsAccessRights();

  // Per-lead row cache keyed by the lead object reference.
  // `referenceStableMergeLeads` in the search hook preserves prev references
  // for unchanged lead IDs across load-more appends, so those leads reuse
  // their cached row and memoized cells skip re-rendering on append.
  // Rows depend on the contacts right too, so the cache is rebuilt when it
  // changes.
  const rowCacheRef = useRef({
    canShowContacts: hasUserRightToShowContacts,
    rows: new WeakMap<ModelLead, LeadsRegistryLeadRow>(),
  });

  useEffect(() => {
    pruneSelection(leads.map(({ Id }) => Id));
  }, [leads, pruneSelection]);

  const tableRows = useMemo<LeadsRegistryTableRow[]>(() => {
    // Both the very first load and a filter change replace the body with a
    // skeleton: the previously accepted rows belong to different filters, so
    // showing them would misrepresent the in-flight result.
    if (loadingMode === 'initial' || loadingMode === 'filter-change') {
      return createLeadsRegistryInitialLoadingSkeletonRows();
    }

    if (rowCacheRef.current.canShowContacts !== hasUserRightToShowContacts) {
      rowCacheRef.current = {
        canShowContacts: hasUserRightToShowContacts,
        rows: new WeakMap<ModelLead, LeadsRegistryLeadRow>(),
      };
    }

    const rowCache = rowCacheRef.current.rows;

    const leadRows: LeadsRegistryTableRow[] = leads.map((lead) => {
      const cachedRow = rowCache.get(lead);

      if (cachedRow) return cachedRow;

      const row = mapModelLeadToLeadsRegistryTableRow(
        lead,
        hasUserRightToShowContacts
      );
      rowCache.set(lead, row);

      return row;
    });

    // Load-more keeps the accepted rows visible and only appends bottom
    // skeleton rows as the next page is fetched.
    if (loadingMode !== 'load-more') {
      return leadRows;
    }

    return [...leadRows, ...createLeadsRegistryLoadingSkeletonRows()];
  }, [hasUserRightToShowContacts, leads, loadingMode]);

  return {
    hasMoreLeads,
    isLoadingMore,
    onLoadMore,
    tableRows,
    totalCount,
  };
};
